/**
 * Una casilla de una zona: o tiene carta, o es un hueco libre.
 *
 * Aquí vive la regla de información oculta del juego: ves tus cartas siempre,
 * incluidas las que acabas de colocar y aún no se han revelado; las del rival
 * solo cuando se destapan. La planificación es simultánea, así que si el reverso
 * filtrase el coste el farol dejaría de existir.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { Undo2 } from 'lucide-react';
import { CardView } from '@/components/cards/CardView';
import { computeCardPower } from '@/engine/effects';
import { useLongPress } from '@/hooks/useLongPress';
import type { BoardCard, PlayerSide, Zone } from '@/types/game';

interface CardSlotProps {
  zone: Zone;
  card: BoardCard | null;
  /** Bando dueño de esta fila. Decide si el reverso tapa o no. */
  side: PlayerSide;
  /** Es la carta que se está revelando en este instante. */
  highlighted?: boolean;
  /** Retirar la carta y recuperar la energía. Solo se pasa si es legal hacerlo. */
  onRecall?: (card: BoardCard) => void;
  /** Mantener pulsada la carta la amplía. También las del rival ya reveladas. */
  onInspect?: (card: BoardCard) => void;
}

export function CardSlot({
  zone,
  card,
  side,
  highlighted = false,
  onRecall,
  onInspect,
}: CardSlotProps) {
  // El hook va antes de cualquier return condicional: React lo exige.
  const longPress = useLongPress(() => {
    if (card && !(!card.revealed && side === 'OPPONENT')) onInspect?.(card);
  }, Boolean(card && onInspect));
  if (!card) {
    return (
      <div
        className="aspect-5/7 w-full rounded-lg border border-dashed border-white/15 bg-white/5"
        aria-hidden
      />
    );
  }

  const hidden = !card.revealed && side === 'OPPONENT';
  const pending = !card.revealed && side === 'PLAYER';
  const canRecall = Boolean(onRecall) && pending;

  const content = (
    <CardView
      card={card}
      variant="board"
      power={computeCardPower(zone, card)}
      faceDown={hidden}
      highlighted={highlighted}
    />
  );

  return (
    // Los gestos van en el contenedor y no en la carta: así funcionan igual
    // sobre una carta propia sin revelar, que no lleva botón de retirada.
    <div className="relative w-full" {...longPress}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          // La clave cambia al revelarse: AnimatePresence hace salir el reverso y
          // entrar la cara, que es lo que se lee como un volteo.
          key={hidden ? 'back' : 'face'}
          initial={{ rotateY: 90, opacity: 0, scale: 0.85 }}
          animate={{ rotateY: 0, opacity: 1, scale: 1 }}
          exit={{ rotateY: -90, opacity: 0, scale: 0.85 }}
          transition={{ duration: 0.28, ease: 'easeOut' }}
          className="w-full [transform-style:preserve-3d]"
        >
          {canRecall ? (
            <button
              type="button"
              onClick={(event) => {
                // La columna entera es zona de suelta y escucha clicks: sin
                // frenar la propagación, retirar una carta jugaría acto seguido
                // la que estuviera seleccionada en la mano.
                event.stopPropagation();
                onRecall?.(card);
              }}
              className="group w-full cursor-pointer"
              title="Retirar la carta y recuperar la energía"
            >
              {content}
              <span className="absolute inset-0 grid place-items-center rounded-lg bg-slate-950/0 transition group-hover:bg-slate-950/60">
                <Undo2 className="h-4 w-4 text-white opacity-0 transition group-hover:opacity-100" />
              </span>
            </button>
          ) : (
            content
          )}
        </motion.div>
      </AnimatePresence>

      {/* Marca discreta de "colocada pero sin revelar", solo en tus propias cartas. */}
      {pending ? (
        <span className="pointer-events-none absolute inset-0 rounded-lg outline-2 outline-offset-0 outline-amber-300/70 outline-dashed" />
      ) : null}
    </div>
  );
}
